import React, { useCallback, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import SlidePanel from "./SlidePanel";

const STATUS = {
  pending: { label: "Awaiting review", cls: "pending" },
  approved: { label: "Approved", cls: "approved" },
  rejected: { label: "Rejected", cls: "rejected" },
  reverted: { label: "Reverted by an admin", cls: "rejected" },
};

const FILTERS = ["all", "pending", "approved", "rejected"];

export default function MySubmissions({ API_BASE, ships, onClose, onViewShip, onNewSubmission }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("wrecks"); // wrecks | edits
  const [edits, setEdits] = useState(null);

  const load = useCallback(() => {
    setError("");
    setItems(null);
    fetch(`${API_BASE}/api/my_submissions`, {
      headers: { Authorization: `Bearer ${Cookies.get("session")}` },
    })
      .then((r) => r.json())
      .then((d) => (Array.isArray(d) ? setItems(d) : setError(d.error || "Failed to load")))
      .catch(() => setError("Failed to load submissions"));
  }, [API_BASE]);

  useEffect(load, [load]);

  useEffect(() => {
    fetch(`${API_BASE}/api/my_edits`, { headers: { Authorization: `Bearer ${Cookies.get("session")}` } })
      .then((r) => r.json())
      .then((d) => setEdits(Array.isArray(d) ? d : []))
      .catch(() => setEdits([]));
  }, [API_BASE]);

  const counts = useMemo(() => {
    const c = { all: 0, pending: 0, approved: 0, rejected: 0 };
    (items || []).forEach((s) => {
      c.all++;
      if (c[s.status] !== undefined) c[s.status]++;
    });
    return c;
  }, [items]);

  const visible = (items || []).filter((s) => filter === "all" || s.status === filter);

  return (
    <SlidePanel
      title="My Submissions"
      subtitle="Track the wrecks you've contributed"
      onClose={onClose}
      actions={<button className="panel-icon-btn" onClick={load} title="Refresh" aria-label="Refresh">↻</button>}
    >
      <div className="panel-tabs view-switch" role="tablist">
        <button className={`panel-tab${view === "wrecks" ? " active" : ""}`} onClick={() => setView("wrecks")}>Wrecks <span>{items ? items.length : "…"}</span></button>
        <button className={`panel-tab${view === "edits" ? " active" : ""}`} onClick={() => setView("edits")}>Suggested edits <span>{edits ? edits.length : "…"}</span></button>
      </div>

      {view === "edits" && (
        <>
          {edits && edits.length === 0 && (
            <div className="panel-empty">
              <div className="panel-empty-icon">✎</div>
              <p>No suggested edits yet.</p>
              <p className="panel-muted">Open any ship on the map and press <strong>✎ Suggest edit</strong>.</p>
            </div>
          )}
          <div className="panel-list">
            {(edits || []).map((e) => {
              const st = STATUS[e.status] || { label: e.status, cls: "pending" };
              const live = ships.find((x) => Number(x.id) === Number(e.ship_id));
              return (
                <div key={e.id} className="panel-item">
                  <div className="panel-item-main">
                    <h3>{e.ship_title || `Ship #${e.ship_id}`}</h3>
                    <div className="panel-meta">
                      <span className={`status-badge ${st.cls}`}>{st.label}</span>
                      <span>{new Date(e.created * 1000).toLocaleDateString()}</span>
                    </div>
                    <p className="panel-hint">Changed: {e.fields.join(", ")}</p>
                    {e.status === "rejected" && e.review_note && <p className="review-reason"><strong>Why it was rejected:</strong> {e.review_note}</p>}
                    {live && <button className="panel-link" onClick={() => onViewShip(live)}>Show on map →</button>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {view === "wrecks" && (<>
      <div className="panel-tabs" role="tablist">
        {FILTERS.map((f) => (
          <button
            key={f}
            role="tab"
            aria-selected={filter === f}
            className={`panel-tab${filter === f ? " active" : ""}`}
            onClick={() => setFilter(f)}
          >
            {f[0].toUpperCase() + f.slice(1)} <span>{counts[f]}</span>
          </button>
        ))}
      </div>

      {error && <p className="panel-error">{error}</p>}
      {!error && items === null && <p className="panel-muted">Loading…</p>}

      {items && items.length === 0 && (
        <div className="panel-empty">
          <div className="panel-empty-icon">⚓</div>
          <p>You haven't submitted any shipwrecks yet.</p>
          <button className="panel-primary" onClick={onNewSubmission}>＋ Submit your first wreck</button>
        </div>
      )}

      {items && items.length > 0 && visible.length === 0 && (
        <p className="panel-muted">No {filter} submissions.</p>
      )}

      <div className="panel-list">
        {visible.map((s) => {
          const st = STATUS[s.status] || { label: s.status, cls: "pending" };
          const live = s.status === "approved" && ships.find((x) => Number(x.id) === Number(s.id));
          return (
            <div key={s.id} className="panel-item">
              <div className="panel-thumb">
                {s.image ? <img src={s.image} alt="" loading="lazy" onError={(e) => (e.currentTarget.style.display = "none")} /> : <span>⚓</span>}
              </div>
              <div className="panel-item-main">
                <h3>{s.title || "Untitled"}</h3>
                <div className="panel-meta">
                  <span className={`status-badge ${st.cls}`}>{st.label}</span>
                  {s.type && s.type !== "unknown" && <span>{s.type}</span>}
                  {s.date && s.date !== "unknown" && <span>{s.date}</span>}
                </div>
                {s.status === "pending" && <p className="panel-hint">An admin will review this soon.</p>}
                {s.status === "rejected" && s.review_note && <p className="review-reason"><strong>Why it was rejected:</strong> {s.review_note}</p>}
                {s.status === "rejected" && <p className="panel-hint">Not added to the map. You can submit a corrected version.</p>}
                {live && (
                  <button className="panel-link" onClick={() => onViewShip(live)}>Show on map →</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      </>)}
    </SlidePanel>
  );
}
